/**
 * Cronos POS Chain API Service
 *
 * Service for interacting with Cronos POS Chain REST API
 * to fetch staking, delegation, and reward data.
 */

import type {
  CronosValidator,
  CronosDelegationResponse,
  CronosRewardsResponse,
  CronosUnbondingResponse,
  CronosBalanceResponse,
  CronosStakingParams,
  CronosWalletData,
  CronosDelegationInfo,
  CronosUnbondingInfo,
  CronosNetworkStats,
  CronosValidatorInfo,
  CRO_DENOM,
} from '../types/cronos.types';

// ============================================
// CONFIGURATION
// ============================================

import { callCoinGeckoAPI } from './api';

const REST_ENDPOINTS = [
  'https://rest.mainnet.cronos-pos.org',
  'https://cronos-pos-rest.publicnode.com',
];

// Rate limiting: 300 req/min for mainnet
const REQUEST_DELAY_MS = 200;

// Cache for validators (they don't change often)
let validatorsCache: CronosValidator[] | null = null;
let validatorsCacheTime = 0;
const VALIDATORS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Convert basecro to CRO (1 CRO = 10^8 basecro)
 */
export function basecroToCro(basecro: string | number): number {
  const value = typeof basecro === 'string' ? parseFloat(basecro) : basecro;
  return value / 1e8;
}

/**
 * Validate Cronos address format
 */
export function isValidCronosAddress(address: string): boolean {
  return /^cro1[a-z0-9]{38}$/.test(address);
}

/**
 * Delay helper for rate limiting
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Fetch with fallback endpoints and retry
 */
async function fetchWithFallback<T>(path: string): Promise<T> {
  let lastError: Error | null = null;

  for (const baseUrl of REST_ENDPOINTS) {
    try {
      const response = await fetch(`${baseUrl}${path}`, {
        headers: { Accept: 'application/json' },
        signal: AbortSignal.timeout(15000),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      lastError = error as Error;
      console.warn(`[CronosAPI] Failed with ${baseUrl}:`, error);
      await delay(REQUEST_DELAY_MS);
    }
  }

  throw lastError || new Error('All endpoints failed');
}

// ============================================
// PRICE API
// ============================================

/**
 * Get CRO price from CoinGecko
 */
export async function getCroPrice(): Promise<number> {
  try {
    const data = await callCoinGeckoAPI('/simple/price', {
      ids: 'crypto-com-chain',
      vs_currencies: 'usd'
    });
    return data['crypto-com-chain']?.usd || 0;
  } catch (error) {
    console.error('[CronosAPI] Failed to fetch CRO price:', error);
    return 0;
  }
}

// ============================================
// STAKING API
// ============================================

/**
 * Get all validators
 */
export async function getValidators(): Promise<CronosValidator[]> {
  // Check cache
  if (validatorsCache && Date.now() - validatorsCacheTime < VALIDATORS_CACHE_TTL) {
    return validatorsCache;
  }

  const allValidators: CronosValidator[] = [];
  let nextKey: string | null = null;

  do {
    const path = nextKey
      ? `/cosmos/staking/v1beta1/validators?pagination.key=${encodeURIComponent(nextKey)}`
      : '/cosmos/staking/v1beta1/validators?pagination.limit=200';

    const response = await fetchWithFallback<{
      validators: CronosValidator[];
      pagination: { next_key: string | null };
    }>(path);

    allValidators.push(...response.validators);
    nextKey = response.pagination?.next_key || null;

    if (nextKey) await delay(REQUEST_DELAY_MS);
  } while (nextKey);

  // Update cache
  validatorsCache = allValidators;
  validatorsCacheTime = Date.now();

  return allValidators;
}

/**
 * Get active (bonded) validators only
 */
export async function getActiveValidators(): Promise<CronosValidator[]> {
  const path = '/cosmos/staking/v1beta1/validators?status=BOND_STATUS_BONDED&pagination.limit=200';
  const response = await fetchWithFallback<{ validators: CronosValidator[] }>(path);
  return response.validators;
}

/**
 * Get validator by operator address
 */
export async function getValidator(validatorAddress: string): Promise<CronosValidator | null> {
  try {
    const path = `/cosmos/staking/v1beta1/validators/${validatorAddress}`;
    const response = await fetchWithFallback<{ validator: CronosValidator }>(path);
    return response.validator;
  } catch (error) {
    console.error(`[CronosAPI] Failed to get validator ${validatorAddress}:`, error);
    return null;
  }
}

/**
 * Get staking parameters
 */
export async function getStakingParams(): Promise<CronosStakingParams> {
  return fetchWithFallback<CronosStakingParams>('/cosmos/staking/v1beta1/params');
}

// ============================================
// DELEGATION API
// ============================================

/**
 * Get all delegations for an address
 */
export async function getDelegations(address: string): Promise<CronosDelegationResponse> {
  const path = `/cosmos/staking/v1beta1/delegations/${address}`;
  return fetchWithFallback<CronosDelegationResponse>(path);
}

/**
 * Get unbonding delegations for an address
 */
export async function getUnbondingDelegations(address: string): Promise<CronosUnbondingResponse> {
  const path = `/cosmos/staking/v1beta1/delegators/${address}/unbonding_delegations`;
  return fetchWithFallback<CronosUnbondingResponse>(path);
}

// ============================================
// REWARDS API
// ============================================

/**
 * Get all delegation rewards for an address
 */
export async function getDelegationRewards(address: string): Promise<CronosRewardsResponse> {
  const path = `/cosmos/distribution/v1beta1/delegators/${address}/rewards`;
  return fetchWithFallback<CronosRewardsResponse>(path);
}

// ============================================
// BALANCE API
// ============================================

/**
 * Get account balances
 */
export async function getBalances(address: string): Promise<CronosBalanceResponse> {
  const path = `/cosmos/bank/v1beta1/balances/${address}`;
  return fetchWithFallback<CronosBalanceResponse>(path);
}

// ============================================
// AGGREGATED DATA
// ============================================

/**
 * Get validator info map for quick lookup
 */
async function getValidatorMap(): Promise<Map<string, CronosValidator>> {
  const validators = await getValidators();
  return new Map(validators.map(v => [v.operator_address, v]));
}

/**
 * Calculate estimated APY for a validator
 */
function calculateValidatorApy(validator: CronosValidator, totalBonded: number): number {
  // Cronos POS Chain typically has ~4-12% APY
  // APY = (block_rewards_per_year / total_staked) * (1 - commission)
  // Using approximate values since exact inflation rate varies
  const baseApy = 0.08; // ~8% base APY
  const commission = parseFloat(validator.commission.commission_rates.rate);
  const validatorTokens = parseFloat(validator.tokens);

  // Adjust based on validator share (larger validators may have slightly lower effective APY)
  const shareOfNetwork = validatorTokens / totalBonded;
  const adjustedApy = baseApy * (1 - commission) * (1 - shareOfNetwork * 0.1);

  return Math.max(adjustedApy, 0);
}

/**
 * Get complete wallet data for a Cronos address
 */
export async function getWalletData(address: string, croPrice: number): Promise<CronosWalletData> {
  // Fetch all data in parallel
  const [balancesRes, delegationsRes, rewardsRes, unbondingRes, validatorMap] = await Promise.all([
    getBalances(address),
    getDelegations(address),
    getDelegationRewards(address),
    getUnbondingDelegations(address),
    getValidatorMap(),
  ]);

  // Calculate total bonded for APY calculation
  const validators = Array.from(validatorMap.values());
  const totalBonded = validators
    .filter(v => v.status === 'BOND_STATUS_BONDED')
    .reduce((sum, v) => sum + parseFloat(v.tokens), 0);

  // Parse available balance
  const croBalance = balancesRes.balances.find(b => b.denom === 'basecro');
  const availableBalance = croBalance ? basecroToCro(croBalance.amount) : 0;

  // Parse delegations
  const delegations: CronosDelegationInfo[] = delegationsRes.delegation_responses.map(d => {
    const validator = validatorMap.get(d.delegation.validator_address);
    const amount = basecroToCro(d.balance.amount);
    const commission = validator
      ? parseFloat(validator.commission.commission_rates.rate) * 100
      : 0;

    // Find rewards for this validator
    const validatorReward = rewardsRes.rewards.find(
      r => r.validator_address === d.delegation.validator_address
    );
    const rewardAmount = validatorReward?.reward.find(r => r.denom === 'basecro');
    const pendingRewards = rewardAmount ? basecroToCro(rewardAmount.amount) : 0;

    return {
      validatorAddress: d.delegation.validator_address,
      validatorMoniker: validator?.description.moniker || 'Unknown',
      validatorCommission: commission,
      delegatedAmount: amount,
      delegatedAmountUsd: amount * croPrice,
      pendingRewards,
      pendingRewardsUsd: pendingRewards * croPrice,
      shares: d.delegation.shares,
    };
  });

  // Parse unbonding delegations
  const unbondings: CronosUnbondingInfo[] = [];
  for (const unbonding of unbondingRes.unbonding_responses || []) {
    const validator = validatorMap.get(unbonding.validator_address);
    for (const entry of unbonding.entries) {
      const amount = basecroToCro(entry.balance);
      const completionTime = new Date(entry.completion_time);
      const remainingMs = completionTime.getTime() - Date.now();
      const remainingDays = Math.max(0, Math.ceil(remainingMs / (1000 * 60 * 60 * 24)));

      unbondings.push({
        validatorAddress: unbonding.validator_address,
        validatorMoniker: validator?.description.moniker || 'Unknown',
        amount,
        amountUsd: amount * croPrice,
        completionTime,
        remainingDays,
      });
    }
  }

  // Calculate totals
  const totalStaked = delegations.reduce((sum, d) => sum + d.delegatedAmount, 0);
  const totalRewardsVal = rewardsRes.total.find(t => t.denom === 'basecro');
  const totalRewards = totalRewardsVal ? basecroToCro(totalRewardsVal.amount) : 0;
  const totalUnbonding = unbondings.reduce((sum, u) => sum + u.amount, 0);
  const totalValue = availableBalance + totalStaked + totalUnbonding + totalRewards;

  // Calculate weighted average APY
  let weightedApy = 0;
  if (totalStaked > 0) {
    for (const d of delegations) {
      const validator = validatorMap.get(d.validatorAddress);
      if (validator) {
        const apy = calculateValidatorApy(validator, totalBonded);
        weightedApy += (d.delegatedAmount / totalStaked) * apy;
      }
    }
  }

  return {
    address,
    availableBalance,
    availableBalanceUsd: availableBalance * croPrice,
    totalStaked,
    totalStakedUsd: totalStaked * croPrice,
    totalRewards,
    totalRewardsUsd: totalRewards * croPrice,
    totalUnbonding,
    totalUnbondingUsd: totalUnbonding * croPrice,
    totalValue,
    totalValueUsd: totalValue * croPrice,
    delegations,
    unbondings,
    validatorCount: delegations.length,
    estimatedApy: weightedApy * 100, // Convert to percentage
    lastUpdated: Date.now(),
  };
}

/**
 * Get wallet data for multiple addresses
 */
export async function getMultipleWalletsData(
  addresses: string[],
  croPrice: number
): Promise<CronosWalletData[]> {
  const results: CronosWalletData[] = [];

  for (const address of addresses) {
    try {
      const walletData = await getWalletData(address, croPrice);
      results.push(walletData);
      await delay(REQUEST_DELAY_MS);
    } catch (error) {
      console.error(`[CronosAPI] Failed to get wallet data for ${address}:`, error);
    }
  }

  return results;
}

/**
 * Get network staking statistics
 */
export async function getNetworkStats(croPrice: number): Promise<CronosNetworkStats> {
  const [validators, params] = await Promise.all([
    getValidators(),
    getStakingParams(),
  ]);

  const bondedValidators = validators.filter(v => v.status === 'BOND_STATUS_BONDED');
  const totalBonded = bondedValidators.reduce((sum, v) => sum + parseFloat(v.tokens), 0);

  // Calculate average APY across all validators
  const apys = bondedValidators.map(v => calculateValidatorApy(v, totalBonded));
  const averageApy = apys.reduce((sum, apy) => sum + apy, 0) / apys.length;

  // Parse unbonding time (in nanoseconds string like "2419200s")
  const unbondingTimeStr = params.params.unbonding_time;
  const unbondingTime = parseInt(unbondingTimeStr.replace('s', ''), 10);

  return {
    totalBonded: basecroToCro(totalBonded),
    totalValidators: validators.length,
    activeValidators: bondedValidators.length,
    bondDenom: params.params.bond_denom,
    unbondingTime,
    averageApy: averageApy * 100, // Convert to percentage
    croPrice,
  };
}

/**
 * Get validator info for UI display
 */
export async function getValidatorsInfo(croPrice: number): Promise<CronosValidatorInfo[]> {
  const validators = await getValidators();
  const bondedValidators = validators.filter(v => v.status === 'BOND_STATUS_BONDED');
  const totalBonded = bondedValidators.reduce((sum, v) => sum + parseFloat(v.tokens), 0);

  return bondedValidators
    .map(v => ({
      operatorAddress: v.operator_address,
      moniker: v.description.moniker,
      identity: v.description.identity,
      website: v.description.website,
      details: v.description.details,
      jailed: v.jailed,
      status: v.status,
      tokens: basecroToCro(v.tokens),
      commission: parseFloat(v.commission.commission_rates.rate) * 100,
      votingPower: (parseFloat(v.tokens) / totalBonded) * 100,
    }))
    .sort((a, b) => b.tokens - a.tokens);
}

// Export API object for convenience
export const cronosApi = {
  getCroPrice,
  getValidators,
  getActiveValidators,
  getValidator,
  getStakingParams,
  getDelegations,
  getUnbondingDelegations,
  getDelegationRewards,
  getBalances,
  getWalletData,
  getMultipleWalletsData,
  getNetworkStats,
  getValidatorsInfo,
  isValidCronosAddress,
  basecroToCro,
};
